import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, User, Mail, Eye, Heart } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const Datenschutz = () => {
  const navigate = useNavigate();

  const handleDonateClick = () => {
    navigate('/#donate');
    setTimeout(() => {
      const element = document.getElementById('donate');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };
  return (
    <div className="min-h-screen bg-warm-beige pt-32 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center space-x-2 text-olive-green hover:text-olive-dark transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Zurück zur Startseite</span>
            </Link>
          </motion.div>

          {/* Datenschutz Content */}
          <motion.div
            className="bg-white rounded-2xl shadow-xl p-12 border border-olive-green/10"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-8">
              <Shield className="w-8 h-8 text-olive-green" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-charcoal font-heading break-words">
                Datenschutzerklärung
              </h1>
            </div>

            <div className="prose prose-lg max-w-none">
              <div className="bg-olive-green/10 p-6 rounded-xl border-l-4 border-olive-green mb-8">
                <p className="text-lg text-charcoal/80 leading-relaxed">
                  Wir nehmen den Schutz eurer Daten sehr ernst. Personenbezogene Daten (z. B. Name, E-Mail, IP-Adresse) 
                  behandeln wir vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading flex items-center space-x-2">
                  <User className="w-6 h-6 text-olive-green" />
                  <span>Verantwortlicher</span>
                </h2>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-charcoal/80 leading-relaxed">
                    <strong>hay - humanity. action. you. e.V.</strong><br />
                    <a href="mailto:info@hayforhumanity.de" className="text-olive-green hover:text-olive-dark transition-colors">
                      info@hayforhumanity.de
                    </a>
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading">
                  1. Erhebung von Daten auf dieser Website
                </h2>
                <div className="space-y-4">
                  <p className="text-charcoal/80 leading-relaxed">
                    Beim Besuch unserer Website werden automatisch Daten wie IP-Adresse, Browsertyp und Uhrzeit gespeichert.
                  </p>
                  <p className="text-charcoal/80 leading-relaxed">
                    Wenn du uns über das Kontaktformular oder per E-Mail kontaktierst, verarbeiten wir die dort eingegebenen 
                    Daten nur zur Bearbeitung deiner Anfrage.
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading">
                  2. Cookies und Analyse-Tools
                </h2>
                <p className="text-charcoal/80 leading-relaxed">
                  Unsere Website verwendet ggf. Cookies, um Funktionen zu gewährleisten. Unsere Website nutzt aus Sicherheitsgründen eine SSL-/TLS-Verschlüsselung. Wir verwenden keine Tracking- oder Werbecookies.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading">
                  3. Eingebettete Inhalte und externe Links
                </h2>
                <div className="space-y-4">
                  <p className="text-charcoal/80 leading-relaxed">
                    Unsere Website verwendet keine eingebetteten Inhalte, die automatisch Daten an Drittanbieter übertragen. 
                    Wir verlinken jedoch zu externen Seiten wie PayPal, WhatsApp, Instagram oder anderen Websites (z. B. Quellenangaben).
                  </p>
                  <p className="text-charcoal/80 leading-relaxed">
                    Wenn du einem dieser Links folgst, gelten die Datenschutzbestimmungen des jeweiligen Anbieters. 
                    Wir haben keinen Einfluss darauf, wie externe Dienste deine Daten verarbeiten.
                  </p>
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading">
                  4. Rechte betroffener Personen
                </h2>
                <p className="text-charcoal/80 leading-relaxed mb-4">
                  Im Rahmen der gesetzlichen Vorgaben stehen dir verschiedene Rechte bezüglich deiner personenbezogenen Daten zu. Dazu gehören insbesondere:
                </p>
                <ul className="list-disc list-inside space-y-2 text-charcoal/80 mb-4">
                  <li>Recht auf Auskunft über gespeicherte personenbezogene Daten</li>
                  <li>Recht auf Berichtigung unrichtiger Daten</li>
                  <li>Recht auf Löschung von Daten, sofern keine gesetzlichen Aufbewahrungspflichten bestehen</li>
                  <li>Recht auf Einschränkung der Verarbeitung in gesetzlich vorgesehenen Fällen</li>
                </ul>
                <p className="text-charcoal/80 leading-relaxed italic">
                  Diese Rechte bestehen gesetzlich – sie müssen nicht aktiv geltend gemacht werden, sofern kein Anlass besteht.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-charcoal mb-4 font-heading">
                  Kontakt zum Datenschutz
                </h2>
                <p className="text-charcoal/80 leading-relaxed">
                  Bei Fragen zum Umgang mit deinen Daten kannst du uns jederzeit kontaktieren:<br />
                  <a href="mailto:info@hayforhumanity.de" className="text-olive-green font-semibold hover:text-olive-dark transition-colors">info@hayforhumanity.de</a>
                </p>
              </div>

              {/* Additional Info */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <p className="text-charcoal/70 text-sm leading-relaxed">
                  Diese Datenschutzerklärung entspricht den aktuellen gesetzlichen Bestimmungen der DSGVO. 
                  Änderungen dieser Datenschutzerklärung behalten wir uns vor und informieren entsprechend auf dieser Seite.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Donate CTA */}
          <motion.div
            className="mt-8 bg-gradient-to-r from-olive-green to-olive-dark rounded-2xl p-8 text-white text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Heart className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4 font-heading">
              Unterstütze unsere Mission.
            </h3>
            <p className="text-lg text-white/90 mb-6 leading-relaxed max-w-2xl mx-auto">
              Jede Spende hilft uns, Menschen in Not zu unterstützen.
            </p>
            <button
              onClick={handleDonateClick}
              className="bg-white text-olive-green hover:bg-cream px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-xl"
            >
              Jetzt spenden
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Datenschutz;